import { T, pinStatusLine, tt, usePinDrop, type LatLng, type ZoneOutline } from '@agos/shared'
import { Spinner, cn } from '@agos/ui'
import { ChevronDown, MapPinned } from 'lucide-react'
import { Suspense, lazy, useState } from 'react'

/**
 * *Mapa* under the customer's delivery address - FRONTEND.md §9.705, owner O81.
 *
 * *"That 'Mapa' should be also shown in Checkout for customer."* The same map as *Nová objednávka*'s (§9.694): it
 * shows the point the address resolved to and lets the customer put the pin on their door. A drop keeps the pin's
 * position, the zone, the fee and the minimum follow from it, and the address text is replaced when the place has a
 * name - `usePinDrop` in `@agos/shared` holds the drop, and the checkout's `delivery-pin.ts` says how long a point
 * belongs to the text beside it.
 *
 * **Collapsed until it is opened, on every screen size, then remembered on this device.** Most checkouts happen on
 * a phone: a map nobody asked for would push the payment step further down, and a one-finger swipe over a map pans
 * the map rather than the page. A desktop gets the same default, so an e2e run - which starts with empty storage -
 * loads no OpenStreetMap tiles it did not ask for.
 *
 * **Leaflet is loaded with the map, not with the page**: `@agos/ui/map` is imported lazily, so the checkout's first
 * paint never carries it and a pickup order never loads it at all.
 */
const PinMap = lazy(() => import('@agos/ui/map').then((module) => ({ default: module.PinMap })))

const OPEN_KEY = 'agos.checkout.map-open'

/** Storage that throws - a private window, a blocked site - is a closed map, not a broken checkout. */
function rememberedOpen(): boolean {
  try {
    return window.localStorage.getItem(OPEN_KEY) === '1'
  } catch {
    return false
  }
}

function rememberOpen(open: boolean): void {
  try {
    window.localStorage.setItem(OPEN_KEY, open ? '1' : '0')
  } catch {
    // The choice then lasts this visit, which is all a device that refuses storage can have.
  }
}

export interface CheckoutPinMapProps {
  /** Where the delivery goes - the point the order will carry, or null when the address has none. */
  point: LatLng | null
  /** The franchise's depot, where the map opens with no point yet. */
  depot: LatLng | null
  /** The pin was put down - the checkout keeps this point as the customer's own place. */
  onPinMoved: (point: LatLng) => void
  /** A provider named the place under the pin - the checkout writes it into the address box. */
  onPinAddress: (address: string) => void
  /**
   * The franchise's delivery zones, drawn under the pin to look at - §9.802, owner O149. `zoneOutlinesOf`'s answer,
   * empty where external couriers deliver. Required, so a caller cannot leave them off without saying so.
   */
  zones: readonly ZoneOutline[]
}

export function CheckoutPinMap({ point, depot, onPinMoved, onPinAddress, zones }: CheckoutPinMapProps) {
  const [open, setOpen] = useState(rememberedOpen)
  const { status, drop } = usePinDrop({ point, onPinMoved, onPinAddress })

  const toggle = () => {
    const next = !open
    setOpen(next)
    rememberOpen(next)
  }

  return (
    <div className="space-y-2" data-checkout-map-panel>
      <button
        type="button"
        data-checkout-map-toggle
        aria-expanded={open}
        onClick={toggle}
        className="min-h-[36px] px-3 inline-flex items-center gap-1.5 rounded-xl border border-border text-xs font-semibold hover:border-primary/40 transition-colors"
      >
        <MapPinned className="w-4 h-4 text-primary" />
        <T>Mapa</T>
        <ChevronDown className={cn('w-4 h-4 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="space-y-1.5">
          <div className="h-64 rounded-2xl overflow-hidden border border-border" data-checkout-map>
            <Suspense
              fallback={
                <div className="w-full h-full flex items-center justify-center">
                  <Spinner />
                </div>
              }
            >
              <PinMap point={point} depot={depot} onDrop={drop} pinLabel={tt('Miesto doručenia')} zones={zones} />
            </Suspense>
          </div>
          <p className="text-xs text-muted-foreground" role="status" data-checkout-map-status={status}>
            {pinStatusLine(status, point !== null)}
          </p>
        </div>
      )}
    </div>
  )
}
